/**
 * ContainerPool
 * Manages a fixed pool of DOM containers for virtual scrolling
 * Handles container recycling, content swapping, and positioning
 */
class ContainerPool {
  constructor(options = {}) {
    // Use centralized config (with fallbacks)
    const cfg =
      typeof InfiniteScrollConfig !== "undefined" ? InfiniteScrollConfig : {};

    // Configuration
    this.maxContainers = options.maxContainers || cfg.MAX_CONTAINERS || 50;
    this.parentSelector = options.parentSelector || "#product-grid";
    this.containerClass = options.containerClass || "virtual-product-item";
    this.skeletonClass = options.skeletonClass || "product-skeleton";
    this.columnsDesktop = options.columnsDesktop || cfg.COLUMNS_DESKTOP || 4;
    this.columnsMobile = options.columnsMobile || cfg.COLUMNS_MOBILE || 2;
    this.mobileBreakpoint = cfg.MOBILE_BREAKPOINT || 750;

    // State
    this.parentElement = null;
    this.containers = [];
    this.containerCount = 0;
    this.totalItems = 0;
    this.loadedCount = 0; // Track how many products are actually loaded

    // Mapping: productIndex -> containerIndex (direct product index, no virtual index)
    this.productToContainer = new Map();
    // Mapping: containerIndex -> productIndex
    this.containerToProduct = new Map();

    // Track which containers are showing skeletons
    this.skeletonContainers = new Set();

    // Track which product index each container has rendered (for skip optimization)
    this.renderedIndices = new Map();

    // Track initial load for animations
    this.isInitialLoad = true;
    this.initialAnimationCount = cfg.INITIAL_ANIMATION_COUNT || 8; // Only animate first N items

    // Product data reference
    this.productData = [];

    // Column tracking for positioning
    this.columns = 2;
    this.itemWidth = 0;
    this.itemHeight = options.itemHeight || cfg.ITEM_HEIGHT_DESKTOP || 320;

    // Cached gap values (set by updateDimensions)
    this.hGap = 16;
    this.vGap = 16;

    this.isInitialized = false;
  }

  /**
   * Initialize the container pool
   * @param {number} totalItems - Total number of items
   * @param {Array} [initialProducts] - Initial product data
   * @returns {boolean} Success status
   */
  initialize(totalItems, initialProducts = []) {
    this.parentElement = document.querySelector(this.parentSelector);
    if (!this.parentElement) {
      return false;
    }

    this.totalItems = totalItems;
    this.productData = initialProducts;
    this.loadedCount = 0; // Reset loaded count

    // Calculate container count: min(50, totalItems)
    this.containerCount = Math.min(this.maxContainers, totalItems);

    // Clear any existing content
    this.clearParent();

    // Create container elements
    this.createContainers();

    // Update dimensions
    this.updateDimensions();

    // Mark initial load for animations
    this.isInitialLoad = true;

    this.isInitialized = true;
    return true;
  }

  /**
   * Clear parent element of existing product items
   */
  clearParent() {
    // Remove existing grid items
    const existingItems = this.parentElement.querySelectorAll(
      ".grid__item, .virtual-product-item"
    );
    existingItems.forEach((item) => item.remove());

    // Clear mappings
    this.productToContainer.clear();
    this.containerToProduct.clear();
    this.skeletonContainers.clear();
    this.renderedIndices.clear();
    this.containers = [];
  }

  /**
   * Create the pool of container elements
   */
  createContainers() {
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < this.containerCount; i++) {
      const container = document.createElement("li");
      container.className = `grid__item ${this.containerClass}`;
      container.dataset.containerIndex = i;
      container.style.position = "absolute";
      container.style.willChange = "transform";
      container.style.contain = "layout style paint";
      container.style.zIndex = "-1"; // Hide empty containers behind content

      // Add animation class only for initial items
      if (this.isInitialLoad && i < this.initialAnimationCount) {
        container.classList.add("scroll-trigger", "animate--slide-in");
        container.style.setProperty("--animation-order", i + 1);
      }

      // Containers start empty - content added when assigned
      // No skeleton rendering here to avoid unassigned containers showing at (0,0)

      this.containers.push(container);
      fragment.appendChild(container);
    }

    this.parentElement.appendChild(fragment);
  }

  /**
   * Update dimensions based on parent width
   */
  updateDimensions() {
    if (!this.parentElement) return;

    const parentWidth = this.parentElement.offsetWidth;
    const isDesktop = window.innerWidth >= this.mobileBreakpoint;
    this.columns = isDesktop ? this.columnsDesktop : this.columnsMobile;

    // Cache gap values from CSS variables (used by positionContainer)
    const computedStyle = getComputedStyle(document.documentElement);
    this.hGap = isDesktop
      ? parseFloat(
          computedStyle.getPropertyValue("--grid-desktop-horizontal-spacing")
        ) || 16
      : parseFloat(
          computedStyle.getPropertyValue("--grid-mobile-horizontal-spacing")
        ) || 8;

    this.vGap = isDesktop
      ? parseFloat(
          computedStyle.getPropertyValue("--grid-desktop-vertical-spacing")
        ) || 16
      : parseFloat(
          computedStyle.getPropertyValue("--grid-mobile-vertical-spacing")
        ) || 8;

    this.itemWidth =
      (parentWidth - this.hGap * (this.columns - 1)) / this.columns;

    // Update container widths
    this.containers.forEach((container) => {
      container.style.width = `${this.itemWidth}px`;
    });
  }

  /**
   * Update visible range - recycle containers as needed
   * @param {Object} range - { start, end } indices to display
   */
  updateVisibleRange(range) {
    if (!this.isInitialized) return;

    // If we have no products loaded yet, don't move containers; let the
    // initial skeleton state stay until the first batch is loaded.
    if (this.productData.length === 0 && this.loadedCount === 0) return;

    const { start, end } = range;
    const neededIndices = new Set();

    // Collect all indices we need to display (capped to loaded count, not total items)
    const maxIndex = this.loadedCount > 0 ? this.loadedCount : this.totalItems;
    for (let i = start; i <= end && i < maxIndex; i++) {
      neededIndices.add(i);
    }

    // Find containers showing indices outside needed range
    const recyclableContainers = [];
    for (const [
      containerIdx,
      productIdx,
    ] of this.containerToProduct.entries()) {
      if (!neededIndices.has(productIdx)) {
        recyclableContainers.push(containerIdx);
      } else {
        neededIndices.delete(productIdx);
      }
    }

    // Find unused containers
    for (let i = 0; i < this.containerCount; i++) {
      if (
        !this.containerToProduct.has(i) &&
        !recyclableContainers.includes(i)
      ) {
        recyclableContainers.push(i);
      }
    }

    // Assign recyclable containers to needed indices
    const neededArray = Array.from(neededIndices).sort((a, b) => a - b);

    for (
      let i = 0;
      i < neededArray.length && i < recyclableContainers.length;
      i++
    ) {
      const productIndex = neededArray[i];
      const containerIndex = recyclableContainers[i];

      this.assignContainer(containerIndex, productIndex);
    }
  }

  /**
   * Assign a container to display a specific product index
   * @param {number} containerIndex
   * @param {number} productIndex
   */
  assignContainer(containerIndex, productIndex) {
    const container = this.containers[containerIndex];
    if (!container) return;

    // Update mappings
    const oldProductIndex = this.containerToProduct.get(containerIndex);
    if (oldProductIndex !== undefined) {
      this.productToContainer.delete(oldProductIndex);
    }

    this.containerToProduct.set(containerIndex, productIndex);
    this.productToContainer.set(productIndex, containerIndex);

    // Position container
    this.positionContainer(container, productIndex);

    // Update content
    this.updateContainerContent(containerIndex, productIndex);
  }

  /**
   * Position a container based on product index
   * Uses cached hGap/vGap from updateDimensions() for performance
   */
  positionContainer(container, productIndex) {
    const row = Math.floor(productIndex / this.columns);
    const column = productIndex % this.columns;

    // Use cached gap values from updateDimensions()
    const top = row * (this.itemHeight + this.vGap);
    const left = column * (this.itemWidth + this.hGap);

    // Set CSS variables for animation
    container.style.setProperty("--transform-x", `${left}px`);
    container.style.setProperty("--transform-y", `${top}px`);

    // Set transform for positioning
    container.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    // Remove data-virtual-index (no longer used)
  }

  /**
   * Update container content with product data or skeleton
   */
  updateContainerContent(containerIndex, productIndex) {
    const container = this.containers[containerIndex];
    if (!container) return;

    const product = this.productData[productIndex];
    const hasProduct = !!product;
    const showingSkeleton = this.skeletonContainers.has(containerIndex);
    const renderedIndex = this.renderedIndices.get(containerIndex);

    // Skip only if already rendered correct content for this EXACT index
    if (renderedIndex === productIndex) {
      if (hasProduct && !showingSkeleton) return; // Already showing this product
      if (!hasProduct && showingSkeleton) return; // Already showing skeleton for this index
    }

    // Remove animation classes for recycled containers (not initial load)
    if (!this.isInitialLoad || productIndex >= this.initialAnimationCount) {
      container.classList.remove("scroll-trigger", "animate--slide-in");
      container.style.removeProperty("--animation-order");
    }

    if (product) {
      container.innerHTML = this.renderProduct(product, {
        index: productIndex,
        lazy: productIndex > 8,
      });
      this.skeletonContainers.delete(containerIndex);
    } else {
      if (!showingSkeleton) {
        container.innerHTML = this.renderSkeleton();
        this.skeletonContainers.add(containerIndex);
      }
    }

    // Remove z-index: -1 once container has content (product or skeleton)
  container.style.zIndex = "";

    // Track what we rendered for this container
    this.renderedIndices.set(containerIndex, productIndex);
  }

  /**
   * Update product data - containers will be updated by updateVisibleRange()
   */
  setProductData(products) {
    this.productData = products;

    // After first data load, disable initial animations for future renders
    if (this.isInitialLoad && products.length > 0) {
      // Delay turning off initial load flag to allow first batch to animate
      setTimeout(() => {
        this.isInitialLoad = false;
      }, 500);
    }

    // DON'T refresh containers here - updateVisibleRange() will handle it
    // This prevents double updates when handleProductsLoaded() calls both
    // setProductData() and handleVisibleRangeChange()
  }

  /**
   * Append new products and refresh skeleton containers
   */
  appendProducts(newProducts, startIndex) {
    for (let i = 0; i < newProducts.length; i++) {
      this.productData[startIndex + i] = newProducts[i];
    }

    for (const [
      containerIdx,
      virtualIdx,
    ] of this.containerToVirtual.entries()) {
      if (
        virtualIdx >= startIndex &&
        virtualIdx < startIndex + newProducts.length
      ) {
        this.updateContainerContent(containerIdx, virtualIdx);
      }
    }
  }

  /**
   * Render skeleton placeholder
   */
  renderSkeleton() {
    return `
      <div class="card-wrapper product-card-wrapper ${this.skeletonClass}">
        <div class="card card--standard card--media">
          <div class="card__inner ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <div class="media skeleton-box" style="padding-bottom: 100%;"></div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <div class="skeleton-text" style="height: 1.2em; width: 80%;"></div>
              <div class="skeleton-text" style="height: 1em; width: 40%; margin-top: 0.5em;"></div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Calculate sale percentage
   */
  calculateSalePercent(price, compareAtPrice) {
    const priceNum = parseFloat(price);
    const compareNum = parseFloat(compareAtPrice);

    if (!compareNum || !priceNum || compareNum <= priceNum) return 0;

    return Math.round((1 - priceNum / compareNum) * 100);
  }

  /**
   * Render product HTML
   */
  renderProduct(product, options = {}) {
    const { lazy = true, index = 0 } = options;

    const hasComparePrice =
      product.compareAtPrice &&
      parseFloat(product.compareAtPrice) > parseFloat(product.price);

    const priceFormatted = this.formatMoney(product.price, true);
    const comparePriceFormatted = hasComparePrice
      ? this.formatMoney(product.compareAtPrice, false)
      : "";

    const salePercent = this.calculateSalePercent(
      product.price,
      product.compareAtPrice
    );

    const imageUrl = product.featuredImage?.url || "";
    const imageAlt = product.featuredImage?.altText || product.title;

    return `
      <div class="card-wrapper product-card-wrapper underline-links-hover">
        <div class="color-scheme-2 card card--card card--media" style="--ratio-percent: 100%;">
          <div class="card__inner ratio" style="--ratio-percent: 100%;">
            ${
              imageUrl
                ? `
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <img
                    src="${imageUrl}"
                    alt="${this.escapeHtml(imageAlt)}"
                    class="motion-reduce"
                    ${lazy ? 'loading="lazy"' : ""}
                    width="400"
                    height="400"
                  >
                </div>
              </div>
            `
                : ""
            }
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="/products/${
                    product.handle
                  }" class="full-unstyled-link">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
              </div>
              ${this.renderBadge(product, salePercent)}
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="/products/${
                  product.handle
                }" class="full-unstyled-link">
                  ${this.escapeHtml(product.title)}
                </a>
              </h3>
              <div class="card-information price-with-add-icon">
                ${this.renderPrice(
                  priceFormatted,
                  comparePriceFormatted,
                  hasComparePrice
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render badge (sale percent or sold out)
   */
  renderBadge(product, salePercent) {
    if (product.available === false) {
      return `
        <div class="card__badge top right">
          <span class="badge badge--bottom-left color-scheme-190ebd1b-3e65-4e9b-b0a3-4e50b4b5bf16">خلصت</span>
        </div>
      `;
    }

    if (salePercent > 0) {
      return `
        <div class="card__badge top right">
          <span class="badge badge--bottom-left color-scheme-f24abe65-5c07-44ec-a8aa-cedce73c8014">-${salePercent}%</span>
        </div>
      `;
    }

    return "";
  }

  /**
   * Render price HTML
   */
  renderPrice(price, compareAtPrice, isOnSale) {
    if (isOnSale) {
      return `
        <div class="price price-dev price--on-sale">
          <div class="price__container">
            <div class="price__sale">
              <span> <s class="price-item price-item--regular">${compareAtPrice}</s></span>
            
              <span class="price-item price-item--sale price-item--last">${price.replace(
                ".",
                "<span class='small-decimals'>."
              )}</span></span>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="price">
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular">${price.replace(
              ".",
              "<span class='small-decimals'>."
            )}</span></span>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Format money value
   */
  formatMoney(amount, withCurrency) {
    const num = parseFloat(amount) || 0;
    if (isNaN(num)) return "";
    const fixedNumber = num.toFixed(2);
    if (withCurrency) {
      return `${fixedNumber} دينار`;
    }
    return fixedNumber;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(str) {
    if (!str) return "";
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Get container by product index
   */
  getContainerByProductIndex(productIndex) {
    const containerIndex = this.productToContainer.get(productIndex);
    return containerIndex !== undefined
      ? this.containers[containerIndex]
      : null;
  }

  /**
   * Update total items
   */
  setTotalItems(newTotal) {
    this.totalItems = newTotal;
  }

  /**
   * Update loaded count (how many products are actually loaded)
   */
  setLoadedCount(count) {
    this.loadedCount = count;
  }

  /**
   * Reset the pool (for filter/sort changes)
   */
  reset() {
    this.productToContainer.clear();
    this.containerToProduct.clear();
    this.skeletonContainers.clear();
    this.renderedIndices.clear();
    this.productData = [];
    this.loadedCount = 0; // Reset loaded count
    this.isInitialLoad = true;

    this.containers.forEach((container, i) => {
      container.innerHTML = ""; // Clear content - will be filled when assigned
      container.style.transform = "translate3d(0, 0, 0)";
      container.style.zIndex = "-1"; // Hide empty containers again

      // Reset animation classes for first 8
      if (i < this.initialAnimationCount) {
        container.classList.add("scroll-trigger", "animate--slide-in");
        container.style.setProperty("--animation-order", i + 1);
      } else {
        container.classList.remove("scroll-trigger", "animate--slide-in");
        container.style.removeProperty("--animation-order");
      }
    });
  }

  /**
   * Destroy and cleanup
   */
  destroy() {
    this.containers.forEach((container) => container.remove());
    this.containers = [];
    this.virtualToContainer.clear();
    this.containerToVirtual.clear();
    this.skeletonContainers.clear();
    this.renderedIndices.clear();
    this.isInitialized = false;
  }

  /**
   * Get current state (for debugging)
   */
  getState() {
    return {
      containerCount: this.containerCount,
      totalItems: this.totalItems,
      productDataLength: this.productData.length,
      assignedContainers: this.containerToVirtual.size,
      skeletonCount: this.skeletonContainers.size,
      columns: this.columns,
      itemWidth: this.itemWidth,
      itemHeight: this.itemHeight,
      isInitialLoad: this.isInitialLoad,
    };
  }
}

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = ContainerPool;
}
